/**
 * @author: lv.chen
 * @date: 2025/4/27
 * @desc: 自定义标签Select组件，多选，支持勾选预定义的标签、自定义标签
 */
import React, { useState, useMemo, useRef } from 'react';
import classnames from 'classnames';
import { useDeepCompareEffect } from 'ahooks';
import { Dropdown, DropdownProps, SelectOption, Modal, Input, Button, FormItem } from 'tea-component';
import { generateMockId } from '@gp/utils/common';
import { defaultOptionTextRenderer } from '@gp/components/Select/utils/renderers';
import GPSelectMultiple from '@gp/components/SelectMultiple';
import type { IBaseProps } from '@home/types/common';
import type { ISelectFilter } from '@gp/types/global';
import { useIntl } from 'umi';
import { isEmpty } from 'lodash-es';
import { NAME_ALREADY_ERR_MSG } from '@gp/constants/const';
import { getDividedOptions } from '@gp/components/SelectMultiple/utils';
import './index.scss';

export interface ICustomizeTagSelectProps extends IBaseProps {
  loading?: boolean;
  ignoreCase?: boolean;
  invalidCustomizeTagChars?: string[];
  invalidCustomizeTagCharsMsg?: string;
  placeholder?: string;
  customizePlaceholder?: string;
  dropdownProps?: DropdownProps;
  deletable?: boolean | ((option: IGPSelectOption) => boolean);
  generateCustomizeAddOptionValue?: (text?: string) => string;
  generateCustomizeAddOptionData?: (value?: string, text?: string) => unknown;
  // option的text的render方法
  optionTextRenderer?: IGPSelectOptionTextRenderer;
  allOption?: false | SelectOption;
  filter?: ISelectFilter;
  options?: IGPSelectOption[] | null;
  value?: any;
  onChange: (value: any) => void;
}
// 最大完整展示已选项内容的个数，超过该数值时，展示已选项的个数
const MAX_SHOW = 1;

const CustomizeTagSelectMultiple: React.FC<ICustomizeTagSelectProps> = (props) => {
  const {
    label,
    showPlaceholder,
    generateCustomizeAddOptionData,
    generateCustomizeDeleteOptionData,
    optionTextRenderer = defaultOptionTextRenderer,
    options: optionsProps,
    value: valueProps,
    onChange: onChangeProps,
    allOptionText = 'All',
    placeholderSelect = '',
    placeholderInput = '',
    style,
  } = props;

  const valueRef = useRef(props); // 每次渲染时更新ref
  valueRef.current = valueProps;
  const [options, setOptions] = useState<any>(optionsProps || []);
  const [isShowCustomize, setIsShowCustomize] = useState(false);
  const [isOpenCustomize, setIsOpenCustomize] = useState(false);
  const [isOpenEngine, setIsOpenEngine] = useState(false);
  const [value, setValue] = useState<any>(valueProps || []);
  // const [valueTemp, setValueTemp] = useState<any>(valueProps || []);
  const [customizeMetricValue, setCustomizeMetricValue] = useState('');

  useDeepCompareEffect(() => {
    if (!isEmpty(optionsProps)) {
      setOptions(optionsProps);
    }
  }, [optionsProps]);

  useDeepCompareEffect(() => {
    if (!isEmpty(valueProps)) {
      setValue(valueProps);
      valueRef.current = valueProps;
    }
  }, [valueProps]);

  const handleInputChange = (val: string) => {
    setCustomizeMetricValue(val);
  };

  const handleCustomize = () => {
    setIsShowCustomize(true);
    setIsOpenCustomize(true);
  };

  const handleCustomizeConfirm = () => {
    const newValue = generateMockId();
    generateCustomizeAddOptionData?.(newValue, customizeMetricValue);
    setOptions([
      ...options,
      {
        is_public: '0',
        text: customizeMetricValue,
        value: newValue,
      },
    ]);
    setValue([...value, newValue]);
    valueRef.current = [...value, newValue];
    setIsShowCustomize(false);
    setIsOpenCustomize(false);
    onChangeProps?.([...value, newValue]);
  };

  const handleCustomizeCancel = () => {
    setIsShowCustomize(false);
    setIsOpenCustomize(false);
    setCustomizeMetricValue('');
  };

  const onChangeHandle = (val: any) => {
    setValue(val);
    valueRef.current = val;
    onChangeProps?.(val);
  };
  const onChangeValueHandle = (val: any) => {
    valueRef.current = val;
  };

  const onOpenHandle = () => {
    setIsOpenEngine(true);
  };
  const onCloseHandle = () => {
    setIsOpenEngine(false);
  };
  const onCancelModal = () => {
    setIsOpenEngine(true);
  };

  const valueLength = useMemo(() => {
    return value?.length || 0;
  }, [value]);

  const optionsLength = useMemo(() => {
    return options?.length || 0;
  }, [options]);

  const handleDeleteTag = (option: any) => {
    const newArray = options?.filter((item: any) => item.value !== option?.value);
    const newValue = valueRef.current?.filter((item: any) => item !== option?.value);
    generateCustomizeDeleteOptionData?.(option);
    setOptions(newArray);
    setValue(newValue);
    valueRef.current = newValue;
    onChangeProps?.(newValue);
  };

  const handleDelete = (e: any, option: any) => {
    e.stopPropagation();
    setIsOpenEngine(true);
    Modal.confirm({
      popupContainer: () => {
        return document.querySelector('.dbg-game-overview-form-input-overlay') as HTMLElement;
      },
      className: classnames('dbg-confirmable-confirm-modal'),
      message: 'Do you want to delete?',
      okText: 'Confirm',
      cancelText: 'Cancel',
      onOk: () => {
        setIsOpenEngine(true);
        handleDeleteTag?.(option);
      },
      onCancel: () => {
        setIsOpenEngine(true);
        onCancelModal();
      },
    });
  };

  const dropdownButtonUI = useMemo(() => {
    return (
      <div className="dbg-common-select-multiple-button">
        {/* 一个都不选时 */}
        {!valueLength && <span className="dbg-common-select-placeholder">{placeholderSelect}</span>}
        {/* 全选，显示All */}
        {valueLength > 0 && valueLength === optionsLength && (
          <span className="select-button-value">{allOptionText}</span>
        )}
        {/* 选择数量大于MAX_SHOW时 并且 不是全选时，显示数量 */}
        {valueLength !== optionsLength && valueLength > MAX_SHOW && (
          <span className="select-button-value">{valueLength}</span>
        )}
        {/* 选择数量小于MAX_SHOW时，展示icon（可无） 和 text */}
        {valueLength !== optionsLength && !!valueLength && valueLength <= MAX_SHOW && (
          <span className="select-button-value">
            {value?.map((item: any, index: any) => {
              const option = options?.find((option: any) => option?.value === item);
              const { text } = option || {};
              return optionTextRenderer?.(option, { splitter: index < valueLength - 1 }) ?? text;
            })}
          </span>
        )}
      </div>
    );
  }, [valueLength, optionsLength]);

  const renderOption = useMemo(() => {
    const dividedOptionsTmp = getDividedOptions(options, value);
    return dividedOptionsTmp?.map((option: any, index: any) => {
      return {
        ...option,
        text: (
          <div
            style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}
            className="dbg-customize-tag-select-multiple-dropdown-btn-div"
            key={index}
          >
            <span className="dbg-customize-tag-select-multiple-dropdown-btn-div-span">{option.text}</span>
            {option?.is_public === '0' && (
              <button
                className="dbg-confirmable-operation"
                onClick={(e) => handleDelete(e, option)}
                style={{ marginLeft: '10px' }}
              >
                <i
                  className="dbg-customize-tag-select-dropdown-panel__checkers-checker-delete-btn tea-icon tea-icon-dismiss"
                  role="img"
                  aria-label="dismiss"
                ></i>
              </button>
            )}
          </div>
        ),
      };
    });
  }, [options, value]);

  const customStatsCur: any = useMemo(() => {
    const hasValue = options?.some(
      (item: any) => item?.text?.trim()?.toLowerCase() === customizeMetricValue?.trim()?.toLowerCase(),
    );
    if (!hasValue) {
      return '';
    }
    return 'error';
  }, [customizeMetricValue, options]);

  const confirmDisabledCur = useMemo(() => {
    if (customStatsCur === 'error') {
      return true;
    }
    return false;
  }, [customStatsCur]);

  return (
    <>
      {isShowCustomize ? (
        <Dropdown
          escapeWithReference
          className="dbi-public-truedata-feedback-customize-dropdown dbg-customize-tag-select-multiple"
          overlayClassName="dbi-public-truedata-feedback-customize-dropdown-overlay"
          size="m"
          appearance="button"
          trigger="click"
          clickClose={false}
          open={isOpenCustomize}
          positionFixed={false}
          button={dropdownButtonUI}
          style={style ? style : { width: 300 }}
        >
          <div className="dbg-customize-tag-select-multiple-dropdown-div">
            <div className="tea-input-password">
              <FormItem
                // label="Publishing Team"
                required={true}
                className="dbg-team-game-information-edit-modal-content-form-select-item"
                // message={'formatComposedTeaFormItemMessage(submitted, [studioField?.meta])'}
                status={customStatsCur}
                message={customStatsCur === 'error' ? NAME_ALREADY_ERR_MSG : ''}
              >
                <Input
                  value={customizeMetricValue}
                  className="dbg-customize-tag-select-multiple-dropdown-div-input"
                  placeholder={placeholderInput}
                  onChange={(val) => handleInputChange(val)}
                />
              </FormItem>
              {/* <div className="tea-input-password__op">
                <SvgIcon type="clock-tower" className="dbi-feedback-btn-icon" />
              </div> */}
            </div>

            <div className="dbi-revenue-growth-btn">
              <Button
                className="dbi-revenue-growth-left"
                type="primary"
                onClick={handleCustomizeConfirm}
                disabled={customizeMetricValue === '' || confirmDisabledCur}
              >
                {'Confirm'}
              </Button>
              <Button className="dbi-revenue-growth-right" onClick={handleCustomizeCancel}>
                {'Back'}
              </Button>
            </div>
          </div>
        </Dropdown>
      ) : (
        <GPSelectMultiple
          value={value}
          placeholder={placeholderSelect}
          className="dbg-game-overview-form-input"
          overlayClassName="dbg-game-overview-form-input-overlay"
          showLabel={false}
          options={options}
          open={isOpenEngine}
          onOpen={onOpenHandle}
          onClose={onCloseHandle}
          renderOption={renderOption}
          showPlaceholder={showPlaceholder}
          onChangePa={(val: any) => onChangeHandle(val)}
          onChangeValue={(val: any) => onChangeValueHandle(val)}
          style={style ? style : { width: '300px' }}
          label={label}
          isNeedEmptyAll={false}
          appearance="button"
          matchButtonWidth
          footer={<a onClick={handleCustomize}>{'Customize'}</a>}
        />
      )}
    </>
  );
};

export default CustomizeTagSelectMultiple;
